import type { BaseLadder } from 'services/assist-api/entities/ladder-data/ladder-datas';
import type { RubricLadderRung } from 'services/assist-api/entities/test-rubric-ladder-datas/types';

export type LadderSection = {
  id: number;
  name: string;
  sectionName: string;
  upperBound: number;
  lowerBound: number;
  type: string;
};

/**
 * Convert `ladder-datas` (`type BaseLadder`) entities to an actual ladder data structure
 *  - a ladder is an array of ladder sections
 *  - a ladder section includes the value for its upper and lower rung as its bounds
 *
 * ```
 *  ――― rung
 *  | |         section including value for upper and lower rung
 *  ――― rung
 *  | |         section including value for upper and lower rung
 *  ――― rung
 * ```
 */
export function transformToLevelLadder(ladderParts: BaseLadder[] = []): LadderSection[] {
  return ladderParts
    .map((ladder: BaseLadder, i: number, arr: Array<BaseLadder>) => ({
      id: ladder.level.id,
      name: `Year ${ladder.level.id}`,
      sectionName: ladder.name,
      upperBound: +ladder.value,
      lowerBound: i === 0 ? 0 : +arr[i - 1].value,
      type: ladder.type
    }))
    .filter((ladder) => ladder.sectionName !== '0')
    .sort((a, b) => a.upperBound - b.upperBound);
}

/**
 * Convert `test-rubric-ladder-datas` (`type RubricLadderRung`) entities to an actual ladder data structure.
 * `testRubricItemOptions` are the main part of this structure with the test-rubric-ladder-datas providing the scale values for each option.
 *  - a ladder is an array of ladder sections
 *  - a ladder section includes the value for its upper and lower rung as its bounds
 *
 * ```
 *  ――― rung
 *  | |         section including value for upper and lower rung
 *  ――― rung
 *  | |         section including value for upper and lower rung
 *  ――― rung
 * ```
 */
export function transformToRubricItemLadder(
  rubricItem: API_JSON & {
    displayName: string;
    testRubricItemOptions: (API_JSON & { score: number })[];
  },
  ladderParts: RubricLadderRung[] = []
): LadderSection[] {
  return rubricItem.testRubricItemOptions
    .map((option) => {
      const rungs = ladderParts;
      const rubricItemRungs = rungs?.filter((r) => r.testRubricItem.id === rubricItem.id);
      const lowerRung = rubricItemRungs?.find((r) => r.name.endsWith(`-to-${option.score}`));
      const upperRung = rubricItemRungs?.find((r) => r.name.startsWith(`${option.score}-to-`));

      return {
        id: option.id,
        name: rubricItem.displayName,
        upperBound: +(upperRung?.value ?? 0),
        lowerBound: +(lowerRung?.value ?? 0),
        sectionName: `${option.score}`,
        type: 'rubric-item-option'
      };
    })
    .sort((a, b) => a.upperBound - b.upperBound);
}
